/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { customStylesAsterisk } from "@components/customStylesInputs/inputsStyles";
import { useEffect, useState } from "react";
import { CivilStatus, MatrimonialRegimesData, PayloadPersonPatch } from "@interfaces/index";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@store/store";
import { useNavigate } from "react-router-dom";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { InactivityHandler } from "@components/InactivityHandler";
import { ApiError } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import { MATRIMONIAL_REGIMEN_CODES } from "@helpers/constantes";
import * as Sentry from "@sentry/react";
import {
  useLazyGetPersonByIdGuaranteeQuery,
  usePatchPersonSecondGuaranteeMutation,
} from "@api/second_guarantee_api/secondGuaranteeApi";
import {
  useLazyGetCivilStatusSecondGuaranteeQuery,
  useLazyGetMatrimonialRegimesSecondGuaranteeQuery,
} from "@api/second_guarantee_api/catalogsSecondGuarantee";

const InformacionSegundoAval = () => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const personAval = useSelector((state: RootState) => state.secondGuarante.guarantee);

  const [getEstadoCivil] = useLazyGetCivilStatusSecondGuaranteeQuery();
  const [getMatrimonialRegimen] = useLazyGetMatrimonialRegimesSecondGuaranteeQuery();
  const [patchPerson] = usePatchPersonSecondGuaranteeMutation();
  const [getPerson] = useLazyGetPersonByIdGuaranteeQuery();

  const [listEdoCivil, setListEdoCivil] = useState<CivilStatus[]>([]);
  const [listMatrimonialRegimen, setListMatrimonialRegimen] = useState<MatrimonialRegimesData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [idEdoCivilCasado, setIdEdoCivilCasado] = useState<string>("");

  const formik = useFormik({
    initialValues: {
      ocupacion: "",
      estado_civil_id: "",
      matrimonial_regimes_id: "",
    },
    validationSchema: yup.object().shape({
      ocupacion: yup.string().required("Campo requerido"),
      estado_civil_id: yup.string().required("Campo requerido"),
      matrimonial_regimes_id: yup.string().when("estado_civil_id", {
        is: idEdoCivilCasado,
        then: (schema) => schema.required("Campo requerido"),
      }),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);

        const payload: PayloadPersonPatch = {
          occupation: values.ocupacion,
          civil_status_id: values.estado_civil_id,
          matrimonial_regimes_id: values.estado_civil_id === idEdoCivilCasado ? values.matrimonial_regimes_id : null,
        };

        await patchPerson({
          personId: String(personAval.id),
          body: payload,
        }).unwrap();

        navigate("/avales/informacion_aval_2");
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "InformacionSegundoAval",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
      }
    },
    validateOnMount: true,
  });

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);

        if (!personAval) {
          navigate("/avales/expediente_digital_segundo_aval");
          return;
        }

        const [res_aval, res_edo_civil, res_edo_matrimonial] = await Promise.all([
          getPerson(String(personAval.id)).unwrap(),
          getEstadoCivil().unwrap(),
          getMatrimonialRegimen().unwrap(),
        ]);

        // buscar id para el estado civil "casado"
        const edo_civil_casado: CivilStatus | undefined = res_edo_civil.find(
          (item) => item.name.toLowerCase() === "casado",
        );
        // buscar id para el regimen "sociedad conyugal"
        const regimen_sociedad_conyugal = res_edo_matrimonial.find(
          (item) => item.code === MATRIMONIAL_REGIMEN_CODES.sociedad_conyugal.code,
        );

        if (!edo_civil_casado || !regimen_sociedad_conyugal) {
          throw new Error("No se encontraron los datos necesarios para continuar.");
        }
        setIdEdoCivilCasado(edo_civil_casado.id);
        setListEdoCivil(res_edo_civil);
        setListMatrimonialRegimen(res_edo_matrimonial);

        formik.setFieldValue("estado_civil_id", res_aval?.civil_status_id || "");
        formik.setFieldValue("matrimonial_regimes_id", res_aval?.matrimonial_regimes_id || "");
        formik.setFieldValue("ocupacion", res_aval?.occupation || "");

        setTimeout(() => {
          formik.validateForm();
        }, 200);
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "InformacionAval-initial_fetch",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8}>
        <InactivityHandler />
        <Box maxWidth="sm" mx="auto" mt={4} px={2}>
          <Typography
            textAlign="center"
            variant={isMobileOrTablet ? "body2" : "body1"}
            fontWeight={700}
            color={theme?.palette?.primary?.dark}
          >
            Información del aval
          </Typography>

          <Typography
            mt={2}
            textAlign="center"
            variant={isMobileOrTablet ? "body2" : "body1"}
            color={theme?.palette?.primary?.dark}
          >
            <strong>1</strong> de 2
          </Typography>

          <Typography
            mt={2}
            textAlign="center"
            variant={isMobileOrTablet ? "body2" : "body1"}
            color={theme?.palette?.primary?.main}
            fontWeight={600}
          >
            {personAval.personalInfo?.name} {personAval.personalInfo?.first_lastname}{" "}
            {personAval.personalInfo?.second_lastname}
          </Typography>

          <form onSubmit={formik.handleSubmit} noValidate>
            <TextField
              id="ocupacion"
              name="ocupacion"
              label="Ocupación"
              margin="normal"
              required
              fullWidth
              autoComplete="off"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              error={formik.touched.ocupacion && Boolean(formik.errors.ocupacion)}
              helperText={formik.touched.ocupacion && formik.errors.ocupacion}
              value={formik.values.ocupacion}
              disabled={isLoading || formik.isSubmitting}
            />

            <FormControl
              fullWidth
              required
              sx={{
                ...customStylesAsterisk,
                mt: 2,
              }}
              error={formik.touched.estado_civil_id && Boolean(formik.errors.estado_civil_id)}
            >
              <InputLabel id="estado_civil_id">Estado civil</InputLabel>
              <Select
                labelId="estado_civil_id"
                id="estado_civil_id"
                name="estado_civil_id"
                fullWidth
                required
                label="Estado civil"
                value={formik.values.estado_civil_id}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                error={formik.touched.estado_civil_id && Boolean(formik.errors.estado_civil_id)}
                disabled={isLoading || formik.isSubmitting}
              >
                <MenuItem value={""}>Selecciona</MenuItem>
                {listEdoCivil.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.estado_civil_id && formik.errors.estado_civil_id && (
                <FormHelperText>{formik.errors.estado_civil_id}</FormHelperText>
              )}
            </FormControl>

            {formik.values.estado_civil_id !== "" && formik.values.estado_civil_id === idEdoCivilCasado && (
              <FormControl
                fullWidth
                required
                sx={{
                  ...customStylesAsterisk,
                  mt: 2,
                }}
                error={formik.touched.matrimonial_regimes_id && Boolean(formik.errors.matrimonial_regimes_id)}
              >
                <InputLabel id="matrimonial_regimes_id">Régimen matrimonial</InputLabel>
                <Select
                  labelId="matrimonial_regimes_id"
                  id="matrimonial_regimes_id"
                  name="matrimonial_regimes_id"
                  fullWidth
                  required
                  label="Régimen matrimonial *"
                  value={formik.values.matrimonial_regimes_id}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  error={formik.touched.matrimonial_regimes_id && Boolean(formik.errors.matrimonial_regimes_id)}
                  disabled={isLoading || formik.isSubmitting}
                >
                  <MenuItem value={""}>Selecciona</MenuItem>
                  {listMatrimonialRegimen.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.matrimonial_regimes_id && formik.errors.matrimonial_regimes_id && (
                  <FormHelperText>{formik.errors.matrimonial_regimes_id}</FormHelperText>
                )}
              </FormControl>
            )}

            <Grid container spacing={1} my={1}>
              <Grid item xs={6} sm={6} md={6}>
                <Button
                  variant="blue_outlined"
                  type="button"
                  fullWidth
                  onClick={() => {
                    navigate("/avales/expediente_digital_segundo_aval");
                  }}
                  disabled={isLoading || formik.isSubmitting}
                  sx={{ padding: "0px", height: "100%" }}
                >
                  Regresar al inicio
                </Button>
              </Grid>

              <Grid item xs={6} sm={6} md={6}>
                <Button variant="default" type="submit" fullWidth disabled={!formik.isValid || formik.isSubmitting}>
                  Continuar
                </Button>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default InformacionSegundoAval;
